/**
 * A route asks for the full-screen layout (no site header, navigation,
 * footer, newsletter or floating prompts) through its `handle`:
 *
 * @example
 * export const handle: FullScreenLayoutHandle = {
 *   fullScreenLayout: (data) => isLiveAttempt(data),
 * };
 *
 * The handle is only the first guess, used by the server render and the
 * hydrating render so they agree; once the page is mounted it reports the
 * real state with `useFullScreenLayout()` and that wins.
 */
export type FullScreenLayoutHandle = {
  fullScreenLayout?: (
    /** This route's loader data. */
    data: unknown,
    /** Every matched route (`id === "root"` holds the root loader data). */
    matches: ReadonlyArray<FullScreenLayoutMatch>,
  ) => boolean;
};

export type FullScreenLayoutMatch = {
  id: string;
  handle?: unknown;
  data?: unknown;
};

/** Whether any matched route's handle asks for the full-screen layout. */
export function guessFullScreenLayout(
  matches: ReadonlyArray<FullScreenLayoutMatch>,
): boolean {
  return matches.some((match) => {
    const ask = (match.handle as FullScreenLayoutHandle | undefined)
      ?.fullScreenLayout;

    if (typeof ask !== "function") return false;

    try {
      return ask(match.data, matches) === true;
    } catch {
      // A malformed payload never hides the site navigation.
      return false;
    }
  });
}

/**
 * The layout to draw: what the mounted page reported, else the route's
 * guess.
 *
 * @example
 * resolveFullScreenLayout(undefined, true); // true (server render)
 * resolveFullScreenLayout(false, true); // false (the page said no)
 */
export function resolveFullScreenLayout(
  requested: boolean | undefined,
  guess: boolean,
): boolean {
  return requested ?? guess;
}
